// Named keys — single source of truth for all client-related storage keys.
// Access via the default-exported `clientStorage.KEYS` object; the named
// `export` was removed because no consumer imports it directly.
const KEYS = Object.freeze({
  ACTIVE_CLIENT_ID: "activeClientId",
  BULK_CONSIGNMENT_ACCESS: "activeClientBulkConsignmentAccess",
  ACTIVE_CLIENT_TYPE: "activeClientType",
  LAST_ACTIVE_CLIENT_ID: "lastActiveClientId",
  CLIENTS: "clients",
  AC: "_ac",
  CLIENT_TYPE: "clientType",
});

const parse = (raw) => {
  if (raw === null) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

// Getters
export const getActiveClientId = () =>
  parse(sessionStorage.getItem(KEYS.ACTIVE_CLIENT_ID));

export const getBulkConsignmentAccess = () =>
  parse(sessionStorage.getItem(KEYS.BULK_CONSIGNMENT_ACCESS));

export const getActiveClientType = () =>
  parse(sessionStorage.getItem(KEYS.ACTIVE_CLIENT_TYPE));

const getLastActiveClientId = () =>
  parse(localStorage.getItem(KEYS.LAST_ACTIVE_CLIENT_ID));

const getClients = () => parse(localStorage.getItem(KEYS.CLIENTS));

// Setters — accessed via the default-exported `clientStorage` object
// (e.g. `clientStorage.setActiveClientId(id)`); no consumer imports them by
// name, so the `export` keyword was dropped to keep them out of knip's
// "unused export" list.
const setActiveClientId = (id) =>
  sessionStorage.setItem(KEYS.ACTIVE_CLIENT_ID, JSON.stringify(id));

const setBulkConsignmentAccess = (value) =>
  sessionStorage.setItem(KEYS.BULK_CONSIGNMENT_ACCESS, JSON.stringify(value));

const setActiveClientType = (type) =>
  sessionStorage.setItem(KEYS.ACTIVE_CLIENT_TYPE, JSON.stringify(type));

const setLastActiveClientId = (id) =>
  localStorage.setItem(KEYS.LAST_ACTIVE_CLIENT_ID, JSON.stringify(id));

const setClients = (clients) =>
  localStorage.setItem(KEYS.CLIENTS, JSON.stringify(clients));

// Compound: full session init from a client object (used on tab restore /
// URL _ac param resolution in getActiveClientId).
export const initFromClient = ({
  clientId,
  clientType,
  hasBulkConsignmentsAccess,
}) => {
  setActiveClientId(clientId);
  setBulkConsignmentAccess(hasBulkConsignmentsAccess ?? 0);
  setActiveClientType(clientType ?? null);
};

const setClientType = (type) =>
  sessionStorage.setItem(KEYS.CLIENT_TYPE, JSON.stringify(type));

const clientStorage = {
  getActiveClientId,
  getBulkConsignmentAccess,
  getActiveClientType,
  getLastActiveClientId,
  getClients,
  setActiveClientId,
  setBulkConsignmentAccess,
  setActiveClientType,
  setLastActiveClientId,
  setClients,
  initFromClient,
  KEYS,
  setClientType,
};

export default clientStorage;
